import { useQuery } from '@apollo/client';
import _ from 'lodash';
import React, { useState } from 'react';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import { DraftsFilter, DraftsOrder } from '../graphql/generated/graphql';
import PostFilterMenu from '../post/PostFilterMenu';
import PostSectionHeader from '../post/PostSectionHeader';
import UIActions from '../ui/UIActions';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import PageMeta from '../utils/PageMeta';
import DraftsList from './DraftsList';
import styles from './DraftsView.module.scss';
import orderIcon from '../icons/order.svg';
import draftsIcon from '../icons/drafts.svg';
import draftArchivedIcon from '../icons/prefilled/draftArchived.svg';
import memberIcon from '../icons/member.svg';
import teamIcon from '../icons/team.svg';
import type { MetadataAttribute } from '../post/PostRow';
import { gql } from '../graphql/generated/gql';

const DRAFTS_VIEW_QUERY = gql(`
  query DraftsView(
    $afterCursor: String
    $filter: DraftsFilter
    $orderBy: DraftsOrder
  ) {
    currentSession {
      # TODO: Fix this the next time the file is edited.
      # eslint-disable-next-line @graphql-eslint/require-id-when-available
      user {
        drafts(
          first: 100
          after: $afterCursor
          filter: $filter
          orderBy: $orderBy
        ) {
          edges {
            node {
              ...DraftsList_drafts
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
        myDraftsCount: draftsCount(filter: MINE)
        sharedDraftsCount: draftsCount(filter: SHARED)
        archivedDraftsCount: draftsCount(filter: ARCHIVED)
        allDraftsCount: draftsCount(filter: ALL)
      }
    }
  }
`);

const METADATA_ATTRIBUTE: Record<DraftsOrder, MetadataAttribute> = {
  [DraftsOrder.RecentlyViewed]: 'userViewedAt',
  [DraftsOrder.RecentlyUpdated]: 'updatedAt',
  [DraftsOrder.RecentlyCreated]: 'insertedAt',
  [DraftsOrder.Alphabetical]: 'updatedAt',
};

const DraftsView = () => {
  const dispatch = useAppDispatch();
  const [filter, setFilter] = useState(DraftsFilter.Mine);
  const orderBy = useAppSelector((state) => state.ui.draftsOrder);
  const setOrderBy = (value: DraftsOrder) =>
    dispatch(UIActions.setDraftsOrder(value));

  const draftsResult = useQuery(DRAFTS_VIEW_QUERY, {
    variables: { filter, orderBy },
  });

  const fetchMoreDrafts = ConnectionHelper.generateFetchMore(
    DRAFTS_VIEW_QUERY,
    draftsResult,
    'currentSession.user.drafts',
  );

  const user = draftsResult.data?.currentSession.user;
  const drafts = _.compact(user?.drafts?.edges?.map((edge) => edge?.node));

  return (
    <div id="content" className="no-padding-x no-padding-y">
      <PageMeta title="Drafts - Slab" />
      <div className={styles.container}>
        <PageContent>
          <PageTitle
            title="Drafts"
            subtitle="Drafts are only visible to you and those you share with. Publish to make them discoverable by your teammates."
          />
        </PageContent>

        <PostSectionHeader
          controls={
            <>
              <PostFilterMenu
                onChange={setFilter}
                options={{
                  [DraftsFilter.Mine]: {
                    icon: memberIcon,
                    label: 'Mine',
                    count: user?.myDraftsCount,
                  },
                  [DraftsFilter.Shared]: {
                    icon: teamIcon,
                    label: 'Shared',
                    count: user?.sharedDraftsCount,
                  },
                  [DraftsFilter.All]: {
                    icon: draftsIcon,
                    count: user?.allDraftsCount,
                    label: 'All Drafts',
                  },
                  [DraftsFilter.Archived]: {
                    icon: draftArchivedIcon,
                    label: 'Archived',
                    count: user?.archivedDraftsCount,
                  },
                }}
                title="Show"
                value={filter}
              />
              <PostFilterMenu
                icon={orderIcon}
                onChange={setOrderBy}
                options={{
                  [DraftsOrder.RecentlyViewed]: {
                    label: 'Recently viewed',
                  },
                  [DraftsOrder.RecentlyUpdated]: {
                    label: 'Recently updated',
                  },
                  [DraftsOrder.RecentlyCreated]: {
                    label: 'Recently created',
                  },
                  [DraftsOrder.Alphabetical]: {
                    label: 'Alphabetical',
                  },
                }}
                menuPlacement="bottom-end"
                title="Sort"
                value={orderBy}
              />
            </>
          }
        >
          <h3>Posts</h3>
        </PostSectionHeader>
        <PageContent>
          {user && (
            <DraftsList
              drafts={drafts}
              fetchMoreDrafts={fetchMoreDrafts}
              metadataAttribute={METADATA_ATTRIBUTE[orderBy]}
            />
          )}
        </PageContent>
      </div>
    </div>
  );
};

export default DraftsView;
